import { useState, useCallback, useMemo, useEffect } from "react";
import { toast } from "sonner";
import { apiClient } from "../lib/api";
import { fetchLastPickAck } from "../lib/api";

/**
 * useWorkbookPickState
 *
 * Custom hook that owns all picks / acceptAll / owner-notes / submitting /
 * submitResult state for a single workbook. Returns the state + actions as
 * a single object so they can be shared between an inline per-question pick
 * widget (rendered next to each Q in the article) and a slim bottom submit
 * bar (rendered after all Q's).
 *
 * Authored 2026-05-23 cycle-5c+25-fix7 per Owner correction:
 *   "the question/answer input is to be under the content where the question
 *    is asked, not all of them at the top. I dont want to keep scrolling up
 *    and down" — required lifting state out of WorkbookPickForm so multiple
 *    inline pick widgets (one per Q, scattered through the article) and the
 *    single bottom submit bar can share one consistent state model.
 *
 * Internal state shape:
 *   picks[qNum] = { letter: "a" | null, free_text: "..." | "" }
 *
 * Submitted payload (per `gitops.PickAck.picks` permissive shape):
 *   - only letter set     → `{ qNum: "a" }`              (legacy bare-letter form)
 *   - free_text set       → `{ qNum: { letter: null, free_text: "..." } }`
 *   - both set            → `{ qNum: { letter: "a", free_text: "..." } }`
 *   - acceptAll path      → `{ __all: "accept_all" }`
 */
export function useWorkbookPickState({ parsed, workbookId, onSubmitted } = {}) {
  const [picks, setPicks] = useState({});
  const [acceptAll, setAcceptAllState] = useState(false);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  // cycle-5c+25-fix13 — prior pick read-back. If the Owner has submitted
  // picks on this workbook before, hydrate the form with the latest ack so
  // re-opening shows what was previously committed instead of an empty form.
  const [priorAck, setPriorAck] = useState(null);

  useEffect(() => {
    if (!workbookId) return;
    let cancelled = false;
    (async () => {
      let ack = null;
      try {
        ack = await fetchLastPickAck(workbookId);
      } catch (e) {
        // Non-404 errors (network blip etc.) — fall through with no
        // hydration. The form just stays empty; submission still works.
        console.warn("Failed to fetch prior workbook pick ack:", e);
        return;
      }
      if (cancelled) return;
      setPriorAck(ack);
      if (!ack) return;
      if (ack.accept_all) {
        setAcceptAllState(true);
        return;
      }
      // Hydrate per-question picks. The ack stores { q_num, letter,
      // option_text, free_text }; the hook stores
      // picks[qNum] = { letter, free_text }.
      const hydrated = {};
      for (const p of ack.picks || []) {
        hydrated[p.q_num] = {
          letter: p.letter || null,
          free_text: p.free_text || "",
        };
      }
      setPicks(hydrated);
    })();
    return () => { cancelled = true; };
  }, [workbookId]);

  const questions = useMemo(() => parsed?.questions || [], [parsed]);
  const hasMpRecs = useMemo(
    () => questions.some((q) => q.mp_rec_letter),
    [questions],
  );

  const isAnswered = useCallback(
    (qNum) => {
      const v = picks[qNum];
      if (!v) return false;
      return Boolean(v.letter) || Boolean((v.free_text || "").trim());
    },
    [picks],
  );

  const allAnswered = useMemo(
    () => acceptAll || questions.every((q) => isAnswered(q.number)),
    [acceptAll, questions, isAnswered],
  );

  const setLetter = useCallback((qNum, letter) => {
    setPicks((prev) => {
      const existing = prev[qNum] || { letter: null, free_text: "" };
      return { ...prev, [qNum]: { ...existing, letter } };
    });
  }, []);

  const setFreeText = useCallback((qNum, text) => {
    setPicks((prev) => {
      const existing = prev[qNum] || { letter: null, free_text: "" };
      return { ...prev, [qNum]: { ...existing, free_text: text } };
    });
  }, []);

  const setAcceptAll = useCallback((value) => {
    setAcceptAllState(value);
    if (value) setPicks({});
  }, []);

  const buildPayload = useCallback(() => {
    if (acceptAll) {
      return {
        workbook_id: workbookId,
        picks: { __all: "accept_all" },
        owner_notes: notes,
      };
    }
    // Normalise to back-compat shape (letter-only stays a bare string).
    const serialised = {};
    for (const q of questions) {
      const v = picks[q.number];
      if (!v) continue;
      const letter = v.letter || null;
      const freeText = (v.free_text || "").trim();
      if (letter && !freeText) {
        serialised[q.number] = letter;
      } else if (letter || freeText) {
        serialised[q.number] = { letter, free_text: freeText };
      }
    }
    return { workbook_id: workbookId, picks: serialised, owner_notes: notes };
  }, [acceptAll, workbookId, notes, questions, picks]);

  const onPreview = useCallback(async () => {
    setSubmitting(true);
    try {
      const { data } = await apiClient.post("/picks/submit", {
        ...buildPayload(),
        dry_run: true,
      });
      setPreview(data);
      setShowPreview(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally {
      setSubmitting(false);
    }
  }, [buildPayload]);

  const onCommit = useCallback(async () => {
    setSubmitting(true);
    try {
      const { data } = await apiClient.post("/picks/submit", buildPayload());
      setSubmitResult(data);
      if (data.pushed) {
        toast.success(
          `Sent to MP for review — ${data.commit_sha?.slice(0, 7)} pushed`,
        );
        if (onSubmitted) onSubmitted(data);
      } else {
        toast.error(data.push_error || "Submission saved locally but push failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  }, [buildPayload, onSubmitted]);

  return {
    // State
    picks,
    acceptAll,
    notes,
    submitting,
    submitResult,
    preview,
    showPreview,
    questions,
    hasMpRecs,
    allAnswered,
    priorAck,
    // Per-question helpers
    isAnswered,
    setLetter,
    setFreeText,
    // Bottom-bar actions
    setAcceptAll,
    setNotes,
    setShowPreview,
    onPreview,
    onCommit,
  };
}
